import React, { ReactNode, useRef, useEffect, useState } from 'react';
import parse from 'html-react-parser';

interface AccordionItemProps {
  title: string;
  text?: string;
  element?: string;
  isOpen: boolean;
  onToggle: () => void;
  children?: ReactNode;
  iconOpen?: string;
  iconClosed?: string;
  iconSize?: number;
  iconPosition?: 'left' | 'right';
  titleClassName?: string;
  className?: string;
  buttonClassName?: string;
  contentClassName?: string;
  textClassName?: string;
  dividerClassName?: string;
  hideDivider?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  text,
  element,
  isOpen,
  onToggle,
  children,
  iconOpen = "/arrow-top.png",
  iconClosed = "/arrow-bottom.png",
  iconSize = 32,
  iconPosition = 'right',
  titleClassName = '',
  className = '',
  buttonClassName = '',
  contentClassName = '',
  textClassName = '',
  dividerClassName = '',
  hideDivider = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState('0px');
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => {
        if (contentRef.current) {
          setHeight(contentRef.current.scrollHeight + 'px');
        }
      }, 10);
    } else {
      setHeight('0px');
      const timeout = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  return (
    <div className={`w-full ${className || 'mt-2'}`}>
      <button 
        type="button" 
        onClick={onToggle}
        className={`${buttonClassName || `w-full flex items-center text-left hover:bg-gray-50 transition-colors duration-200 rounded-lg p-2 ${iconPosition === 'left' ? 'flex-row gap-4' : 'flex-row justify-between'}`}`}
      >
        {iconPosition === 'left' && (
          <img
            src={isOpen ? iconOpen : iconClosed}
            width={iconSize}
            height={iconSize}
            alt={isOpen ? "Retrair" : "Expandir"}
            className="shrink-0"
          />
        )}
        <p className={`${buttonClassName ? '' : 'max-w-[calc(90%)] text-lg'} ${titleClassName}`}>{title}</p>
        {iconPosition === 'right' && (
          <img
            src={isOpen ? iconOpen : iconClosed}
            width={iconSize}
            height={iconSize}
            alt={isOpen ? "Retrair" : "Expandir"}
          />
        )}
      </button>
      <div
        ref={contentRef}
        style={{
          maxHeight: height,
          opacity: isOpen ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s',
        }}
      >
        {shouldRender && (
          <div className={contentClassName}>
            {children}
            {element && parse(element)}
            {text && (
              <p className={textClassName || "text-base text-gray-500"}>
                {text.split("\n").map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            )}
          </div>
        )}
      </div>
      {!hideDivider && (
        <div className={dividerClassName || "h-px w-full bg-gray-300 mt-2 mb-2"} />
      )}
    </div>
  );
};

export default React.memo(AccordionItem); 